import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../config/firebaseConfig';
import { toast } from 'react-hot-toast';
import {
  LuCheck,
  LuCloudUpload,
  LuFile,
  LuFileImage,
  LuFileText,
  LuLoaderCircle,
  LuPackageOpen,
  LuSave,
  LuTrash2,
} from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import IconButton from '../../components/ui/IconButton';
import Modal from '../../components/ui/Modal';
import { Field, TextArea, TextInput } from '../../components/form/FormControls';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  CARRIERS,
  SHIPMENT_ALLOWED_FILE_TYPES,
  SHIPMENT_MAX_FILE_SIZE,
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_OPTIONS,
  formatFileSize,
  parseShipmentCost,
  todayInputValue,
} from '../../utils/shipments';
import { NativeSelect, PANEL } from './visitModal/VisitUi';

const emptyForm = {
  subject: '',
  content: '',
  status: SHIPMENT_STATUS.NOT_SENT,
  carrier: '',
  trackingNumber: '',
  shippedAt: '',
  deliveredAt: '',
  cost: '',
  recipientName: '',
  recipientOrg: '',
  recipientPhone: '',
  destination: '',
  senderName: '',
  note: '',
  attachments: [],
};

const fileIconFor = (type = '') => {
  if (type.startsWith('image/')) return LuFileImage;
  if (type === 'application/pdf') return LuFileText;
  return LuFile;
};

const STATUS_TONES = {
  neutral: 'tw-border-slate-400/50 tw-bg-slate-500/10 tw-text-slate-800 dark:tw-text-slate-100',
  info: 'tw-border-sky-500/50 tw-bg-sky-500/10 tw-text-sky-800 dark:tw-text-sky-200',
  success: 'tw-border-emerald-500/50 tw-bg-emerald-500/10 tw-text-emerald-800 dark:tw-text-emerald-200',
  danger: 'tw-border-rose-500/50 tw-bg-rose-500/10 tw-text-rose-800 dark:tw-text-rose-200',
};

const FORM_SECTION = `${PANEL} tw-space-y-4 tw-p-4`;
const SECTION_TITLE = 'tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';

/**
 * Kargo kaydı ekleme / düzenleme formu.
 * Kaydetmeyi çağıran tarafa bırakır (onSave); yalnızca dosyalar doğrudan
 * Storage'a yüklenir, böylece kullanıcı yüklemeyi anında görür.
 */
const ShipmentModal = ({ show, onHide, visitId, shipment, onSave, defaultSender }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`visitModal.shipments.dialog.${key}`, vars);
  const carrierName = (carrier) => (['kurye', 'diger'].includes(carrier.value) ? t(`visitModal.shipments.carriers.${carrier.value}`) : carrier.label);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const isEditing = Boolean(shipment);

  useEffect(() => {
    if (!show) return;
    if (shipment) {
      setFormData({
        ...emptyForm,
        ...shipment,
        // Bedel sayı olarak saklanır; formda Türkçe ondalık yazımıyla gösterilir
        cost: shipment.cost == null ? '' : String(shipment.cost).replace('.', ','),
      });
    } else {
      setFormData({ ...emptyForm, senderName: defaultSender || '' });
    }
    setErrors({});
  }, [show, shipment, defaultSender]);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  // Durum değişince tarihler otomatik dolar; kullanıcı yine de değiştirebilir.
  const handleStatusChange = (status) => {
    setFormData((prev) => {
      const next = { ...prev, status };
      if (status !== SHIPMENT_STATUS.NOT_SENT && !prev.shippedAt) {
        next.shippedAt = todayInputValue();
      }
      if (status === SHIPMENT_STATUS.DELIVERED && !prev.deliveredAt) {
        next.deliveredAt = todayInputValue();
      }
      if (status !== SHIPMENT_STATUS.DELIVERED) {
        next.deliveredAt = status === SHIPMENT_STATUS.RETURNED ? prev.deliveredAt : '';
      }
      return next;
    });
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const accepted = [];
    files.forEach((file) => {
      if (!SHIPMENT_ALLOWED_FILE_TYPES.includes(file.type)) {
        toast.error(`${file.name}: desteklenmeyen dosya türü`);
        return;
      }
      if (file.size > SHIPMENT_MAX_FILE_SIZE) {
        toast.error(`${file.name}: dosya 10 MB sınırını aşıyor`);
        return;
      }
      accepted.push(file);
    });
    if (accepted.length === 0) return;

    setUploading(true);
    try {
      const uploaded = await Promise.all(
        accepted.map(async (file) => {
          const storagePath = `shipments/${visitId}/${Date.now()}_${file.name}`;
          const storageRef = ref(storage, storagePath);
          const snapshot = await uploadBytes(storageRef, file);
          const url = await getDownloadURL(snapshot.ref);
          return {
            name: file.name,
            url,
            type: file.type,
            size: file.size,
            storagePath,
            uploadedAt: new Date().toISOString(),
          };
        })
      );
      setFormData((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...uploaded],
      }));
      toast.success(
        uploaded.length > 1 ? `${uploaded.length} dosya yüklendi` : 'Dosya yüklendi'
      );
    } catch (error) {
      console.error('Error uploading shipment file:', error);
      toast.error('Dosya yüklenirken bir hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (index) => {
    const attachment = (formData.attachments || [])[index];
    if (!attachment) return;
    try {
      if (attachment.storagePath) {
        await deleteObject(ref(storage, attachment.storagePath));
      }
    } catch (error) {
      // Dosya Storage'dan silinemese bile kayıttan düşürülür; aksi halde
      // kullanıcı yanlış yüklediği dosyadan kurtulamaz.
      console.error('Error deleting shipment file:', error);
    }
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.subject.trim()) nextErrors.subject = 'Konu başlığı zorunludur';
    if (!formData.recipientName.trim()) nextErrors.recipientName = 'Alıcı zorunludur';
    if (formData.status !== SHIPMENT_STATUS.NOT_SENT && !formData.carrier) {
      nextErrors.carrier = 'Kargoya verildiyse firma seçin';
    }
    if (Number.isNaN(parseShipmentCost(formData.cost))) {
      nextErrors.cost = 'Geçerli bir tutar girin (örn. 125,50)';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (uploading) {
      toast.error('Dosya yüklemesi bitene kadar bekleyin');
      return;
    }
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave({
        ...formData,
        subject: formData.subject.trim(),
        trackingNumber: formData.trackingNumber.trim(),
        cost: parseShipmentCost(formData.cost),
        recipientName: formData.recipientName.trim(),
      });
      onHide();
    } catch (error) {
      // Hata mesajı çağıran tarafta gösteriliyor
    } finally {
      setSaving(false);
    }
  };

  const attachments = formData.attachments || [];

  return (
    <Modal
      isOpen={Boolean(show)}
      onClose={() => !saving && onHide()}
      size="lg"
      title={isEditing ? label('titleEdit') : label('titleNew')}
      subtitle={label('subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-indigo-400 tw-to-blue-600 tw-shadow-indigo-500/30" size="xs">
          <LuPackageOpen aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onHide} disabled={saving}>{label('cancel')}</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving || uploading} className="max-sm:tw-flex-1">
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('saving') : isEditing ? label('update') : label('save')}
          </Button>
        </>
      )}
    >
      <div className="tw-space-y-4">
        {/* 1. Gönderi içeriği */}
        <section className={FORM_SECTION}>
          <h3 className={SECTION_TITLE}>{label('sections.details')}</h3>
          <Field label={label('subject')} htmlFor="shipmentSubject" required error={errors.subject}>
            <TextInput
              id="shipmentSubject"
              type="text"
              value={formData.subject}
              onChange={(e) => setField('subject', e.target.value)}
              placeholder={label('subjectPlaceholder')}
              maxLength={120}
              error={errors.subject}
            />
          </Field>
          <Field label={label('content')} htmlFor="shipmentContent">
            <TextArea
              id="shipmentContent"
              rows={3}
              value={formData.content}
              onChange={(e) => setField('content', e.target.value)}
              placeholder={label('contentPlaceholder')}
            />
          </Field>
        </section>

        {/* 2. Kargo durumu */}
        <section className={FORM_SECTION}>
          <h3 className={SECTION_TITLE}>{label('sections.status')}</h3>
          <div role="radiogroup" aria-label={label('sections.status')} className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
            {SHIPMENT_STATUS_OPTIONS.map((option) => {
              const selected = formData.status === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => handleStatusChange(option.value)}
                  className={`tw-flex tw-min-h-[2.75rem] tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-2xl tw-border tw-px-3 tw-py-2 tw-text-center tw-text-xs tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                    selected
                      ? STATUS_TONES[option.tone]
                      : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-600 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.07]'
                  }`}
                >
                  {selected && <LuCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />}
                  {t(`visitModal.shipments.status.${option.value}`)}
                </button>
              );
            })}
          </div>
          <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('statusHint')}</p>

          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
            <Field label={label('carrier')} htmlFor="shipmentCarrier" error={errors.carrier}>
              <NativeSelect
                id="shipmentCarrier"
                value={formData.carrier}
                onChange={(e) => setField('carrier', e.target.value)}
                aria-invalid={errors.carrier ? true : undefined}
                className={errors.carrier ? 'tw-ring-2 tw-ring-rose-500/50' : ''}
              >
                <option value="">{label('carrierPlaceholder')}</option>
                {CARRIERS.map((carrier) => (
                  <option key={carrier.value} value={carrier.value}>
                    {carrierName(carrier)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={label('trackingNumber')} htmlFor="shipmentTracking">
              <TextInput
                id="shipmentTracking"
                type="text"
                value={formData.trackingNumber}
                onChange={(e) => setField('trackingNumber', e.target.value)}
                placeholder={label('trackingPlaceholder')}
              />
            </Field>
          </div>

          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3">
            <Field label={label('shippedAt')} htmlFor="shipmentShippedAt">
              <TextInput
                id="shipmentShippedAt"
                type="date"
                value={formData.shippedAt}
                onChange={(e) => setField('shippedAt', e.target.value)}
              />
            </Field>
            <Field label={label('deliveredAt')} htmlFor="shipmentDeliveredAt">
              <TextInput
                id="shipmentDeliveredAt"
                type="date"
                value={formData.deliveredAt}
                min={formData.shippedAt || undefined}
                onChange={(e) => setField('deliveredAt', e.target.value)}
              />
            </Field>
            <Field label={label('cost')} htmlFor="shipmentCost" error={errors.cost}>
              <span className="tw-relative tw-block">
                <span className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-sm tw-font-semibold tw-text-slate-400">₺</span>
                <TextInput
                  id="shipmentCost"
                  type="text"
                  inputMode="decimal"
                  value={formData.cost}
                  onChange={(e) => setField('cost', e.target.value)}
                  placeholder={label('costPlaceholder')}
                  error={errors.cost}
                  className="tw-pl-8"
                />
              </span>
            </Field>
          </div>
        </section>

        {/* 3. Kime / nereye */}
        <section className={FORM_SECTION}>
          <h3 className={SECTION_TITLE}>{label('sections.recipient')}</h3>
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
            <Field label={label('recipientName')} htmlFor="shipmentRecipient" required error={errors.recipientName}>
              <TextInput
                id="shipmentRecipient"
                type="text"
                value={formData.recipientName}
                onChange={(e) => setField('recipientName', e.target.value)}
                placeholder={label('recipientNamePlaceholder')}
                error={errors.recipientName}
              />
            </Field>
            <Field label={label('recipientOrg')} htmlFor="shipmentRecipientOrg">
              <TextInput
                id="shipmentRecipientOrg"
                type="text"
                value={formData.recipientOrg}
                onChange={(e) => setField('recipientOrg', e.target.value)}
                placeholder={label('recipientOrgPlaceholder')}
              />
            </Field>
            <Field label={label('recipientPhone')} htmlFor="shipmentPhone">
              <TextInput
                id="shipmentPhone"
                type="tel"
                value={formData.recipientPhone}
                onChange={(e) => setField('recipientPhone', e.target.value)}
                placeholder={label('recipientPhonePlaceholder')}
              />
            </Field>
            <Field label={label('senderName')} htmlFor="shipmentSender">
              <TextInput
                id="shipmentSender"
                type="text"
                value={formData.senderName}
                onChange={(e) => setField('senderName', e.target.value)}
                placeholder={label('senderNamePlaceholder')}
              />
            </Field>
          </div>
          <Field label={label('destination')} htmlFor="shipmentDestination">
            <TextArea
              id="shipmentDestination"
              rows={2}
              value={formData.destination}
              onChange={(e) => setField('destination', e.target.value)}
              placeholder={label('destinationPlaceholder')}
            />
          </Field>
        </section>

        {/* 4. Ekler */}
        <section className={FORM_SECTION}>
          <h3 className={SECTION_TITLE}>{label('sections.files')}</h3>
          <div
            role="button"
            tabIndex={0}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              uploadFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-dashed tw-px-4 tw-py-6 tw-text-center tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
              dragActive
                ? 'tw-border-teal-500 tw-bg-teal-500/[0.07]'
                : 'tw-border-slate-300 tw-bg-slate-50/60 hover:tw-border-teal-400 hover:tw-bg-teal-500/[0.04] dark:tw-border-white/15 dark:tw-bg-white/[0.02]'
            }`}
          >
            {uploading
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-8 tw-w-8 tw-animate-spin tw-text-teal-500" />
              : <LuCloudUpload aria-hidden="true" className="tw-h-8 tw-w-8 tw-text-teal-500" />}
            <span className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
              {uploading ? label('uploading') : label('drop')}
            </span>
            <span className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{label('dropHint')}</span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              accept={SHIPMENT_ALLOWED_FILE_TYPES.join(',')}
              onChange={(e) => {
                uploadFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>

          {attachments.length > 0 && (
            <ul className="tw-space-y-2">
              {attachments.map((file, index) => {
                const Icon = fileIconFor(file.type);
                return (
                  <li key={file.storagePath || `${file.name}-${index}`} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/80 tw-py-1.5 tw-pl-3 tw-pr-1.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                    <Icon aria-hidden="true" className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-slate-400" />
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium tw-text-sky-700 hover:tw-underline dark:tw-text-sky-300"
                    >
                      {file.name}
                    </a>
                    <span className="tw-shrink-0 tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{formatFileSize(file.size)}</span>
                    <IconButton label={label('removeFile')} tone="alert" onClick={() => removeAttachment(index)}>
                      <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                    </IconButton>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className={FORM_SECTION}>
          <Field label={label('note')} htmlFor="shipmentNote">
            <TextArea
              id="shipmentNote"
              rows={2}
              value={formData.note}
              onChange={(e) => setField('note', e.target.value)}
              placeholder={label('notePlaceholder')}
            />
          </Field>
        </section>
      </div>
    </Modal>
  );
};

ShipmentModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  visitId: PropTypes.string,
  shipment: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  defaultSender: PropTypes.string,
};

export default ShipmentModal;
